import { Category } from '@/models/category.model';
import { Media } from '@/models/media.model';
import { Link } from './link.model';
import { UserCo2QuizAnswer } from '@/models/user.model';
import { UserLabel } from './label.model';

export enum QuestionType {
  Answers = 'answers',
  PartsQuestion = 'parts-question',
  UserInput = 'user-input',
}

export const questionTypeItems = [
  { title: 'Answers', value: QuestionType.Answers },
  { title: 'User input', value: QuestionType.UserInput },
  { title: 'Parts Question', value: QuestionType.PartsQuestion },
];

export enum CalculationLabel {
  AppliancePowerUsage = 'appliance-power-usage',
  FamilySize = 'family-size',
  PowerType = 'power-type',
  HouseAge = 'house-age',
  HouseType = 'house-type',
  HouseOccupants = 'house-occupants',
  Heating = 'heating',
  SolarPanels = 'solar-panels',
  FruitAndVeg = 'fruit-and-veg',
  Organic = 'organic',
  FoodWaste = 'foodwaste',
  CarPassengers = 'car-passengers',
  MotorPassengers = 'motor-passengers',
  MotorFuel = 'motor-fuel',
  CarKM = 'car-km',
  CarFuel = 'car-fuel',
  CarType = 'car-type',
  CarSharing = 'car-sharing',
}

export const calculationLabelItems = [
  {
    title: 'Appliance power usage',
    value: CalculationLabel.AppliancePowerUsage,
  },
  { title: 'Family size', value: CalculationLabel.FamilySize },
  { title: 'Power type', value: CalculationLabel.PowerType },
  { title: 'Heating', value: CalculationLabel.Heating },
  { title: 'Solar panels', value: CalculationLabel.SolarPanels },
  { title: 'Fruits and vegetables', value: CalculationLabel.FruitAndVeg },
  { title: 'Organic', value: CalculationLabel.Organic },
  { title: 'Food Waste', value: CalculationLabel.FoodWaste },
  { title: 'Car Passengers', value: CalculationLabel.CarPassengers },
  { title: 'Motor Passengers', value: CalculationLabel.MotorPassengers },
  { title: 'Motor Fuel', value: CalculationLabel.MotorFuel },
  { title: 'House Occupants', value: CalculationLabel.HouseOccupants },
  { title: 'House Age', value: CalculationLabel.HouseAge },
  { title: 'House Type', value: CalculationLabel.HouseType },
  { title: 'Car Km', value: CalculationLabel.CarKM },
  { title: 'Car Fuel', value: CalculationLabel.CarFuel },
  { title: 'Car Type', value: CalculationLabel.CarType },
  { title: 'Car Sharing', value: CalculationLabel.CarSharing },
].sort((a, b) => (a.title < b.title ? -1 : 1));

export enum CalculationType {
  Value = 'value',
  ValueFactor = 'value-factor',
  Diet = 'diet',
  Mobility = 'mobility',
  Car = 'car',
  Flying = 'flying',
  Motor = 'motor',
  HouseSize = 'houseSize',
  Consumption = 'consumption',
  Electricity = 'electricity',
  AquariumElectricity = 'aquariumElectricity',
  GasUsage = 'gasUsage',
  ElectricityUsage = 'electricityUsage',
}

export const calculationTypeItems = [
  {
    title: 'Value or Input',
    value: CalculationType.Value,
  },
  {
    title: 'Value * Input',
    value: CalculationType.ValueFactor,
  },
  { title: 'Diet', value: CalculationType.Diet },
  { title: 'Mobility', value: CalculationType.Mobility },
  { title: 'Car', value: CalculationType.Car },
  { title: 'Flying', value: CalculationType.Flying },
  { title: 'Motor', value: CalculationType.Motor },
  { title: 'House Size', value: CalculationType.HouseSize },
  { title: 'Consumption', value: CalculationType.Consumption },
  { title: 'Electricity', value: CalculationType.Electricity },
  { title: 'Gas Usage', value: CalculationType.GasUsage },
  { title: 'Electricity Usage', value: CalculationType.ElectricityUsage },
  {
    title: 'Electricity (Aquarium)',
    value: CalculationType.AquariumElectricity,
  },
].sort((a, b) => (a.title < b.title ? -1 : 1));

export interface Co2QuizQuestion {
  id: number;
  creationDate?: Date;
  updatedDate?: Date;
  title: string;
  description: string;
  hint: string;
  index: number;
  unitValue: number;
  averageValue: number;
  isQuickScan: boolean;
  isDismissable: boolean;
  isValue: boolean;
  questionType: QuestionType;
  calculationType: CalculationType;
  calculationLabels: CalculationLabel[];
  categories: Category[];
  answers: Co2QuizAnswer[];
  relatedQuestions: Co2QuizQuestion[];
  chainQuestion?: Co2QuizQuestion;
  parentQuestion?: Co2QuizQuestion;
  partQuestion?: Co2QuizQuestion;
  partQuestions?: Co2QuizQuestion[];
  sources: Link[];
  media: Media[];
}

export interface Co2QuizAnswer {
  id?: number;
  creationDate?: Date;
  updatedDate?: Date;
  index: number;
  text: string;
  unitAmount: number;
  isAverage: boolean;
  isOther: boolean;
  followUpQuestion?: Co2QuizQuestion;
  resetsQuestions?: Co2QuizQuestion[];
  providesLabels: UserLabel[];
  calculationLabels: string[];
}

export const titleRules = [
  (value: string) => {
    if (value) return true;

    return 'Title is required.';
  },
];

export function getSimpleCalculationType(v: Co2QuizQuestion): string {
  const type = calculationTypeItems.find((i) => i.value === v.calculationType);
  return type?.title || '';
}

export interface Co2Results {
  id: number;
  creationDate: string;
  updatedDate: string;
  results: (Co2QuizResults | SimpleCo2QuizResults)[];
  resultsDate: string;
  isValid: boolean;
  isComplete: boolean;
}

export interface Co2QuizResults {
  id: number;
  parentCategoryId: number | null;
  level: number;
  title: string;
  emoji: string;
  co2value: number;
  question?: Co2QuizQuestion;
  relatedQuestions: Co2QuizQuestion[];
  value?: Co2BasketValue;
  average?: Co2CountryAverage;
  userAnswer?: UserCo2QuizAnswer;
}

interface SimpleCo2QuizResults {
  id: number;
  parentCategoryId: number | null;
  co2value: number;
}

export interface Co2BasketValue {
  id: number;
  creationDate?: string;
  updatedDate?: string;
  value: number;
  description: string;
  sources: Link[];
  country: string;
  overrideQuestions: Co2QuizQuestion[];
  notRelevantForlabels: UserLabel[];
  categories: Category[];
}

export interface Co2CountryAverage {
  id: number;
  creationDate?: string;
  updatedDate?: string;
  value: number;
  country: string;
  category: Category;
}
